import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { FiStar, FiMapPin, FiUsers, FiHome, FiCheck, FiHeart, FiShare2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useBooking } from '../context/BookingContext';
import { useAuth } from '../context/AuthContext';

const ApartmentDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setApartment, setDates, setGuests, setPricing, bookingData } = useBooking();
  const { isUserAuthenticated } = useAuth();

  const [apartment, setApartmentData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  const [checkIn, setCheckIn] = useState(bookingData.checkIn);
  const [checkOut, setCheckOut] = useState(bookingData.checkOut);
  const [guests, setGuestsState] = useState(bookingData.guests || { adults: 1, children: 0 });
  const [pricing, setPricingState] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    const fetchApartment = async () => {
      try {
        const data = await api.getApartmentBySlug(slug);
        setApartmentData(data);
        setApartment(data);

        const reviewsData = await api.getApartmentReviews(data._id);
        setReviews(reviewsData.reviews || []);
      } catch (error) {
        toast.error('Apartment not found');
        navigate('/apartments');
      } finally {
        setLoading(false);
      }
    };
    fetchApartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const calculatePrice = async () => {
      if (!apartment || !checkIn || !checkOut) return;

      setCalculating(true);
      try {
        const data = await api.calculatePrice({
          apartmentId: apartment._id,
          checkIn: checkIn.toISOString(),
          checkOut: checkOut.toISOString(),
          guests
        });
        setPricingState(data);
        setPricing(data);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setCalculating(false);
      }
    };
    calculatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartment, checkIn, checkOut, guests]);

  const handleBook = () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setDates(checkIn, checkOut);
    setGuests(guests);
    navigate(`/booking/${apartment._id}`);
  };

  const handleSave = async () => {
    if (!isUserAuthenticated()) {
      toast.info('Please login to save apartments');
      return;
    }

    try {
      if (isSaved) {
        await api.removeSavedApartment(apartment._id);
        toast.success('Removed from saved');
      } else {
        await api.saveApartment(apartment._id);
        toast.success('Saved to favorites');
      }
      setIsSaved(!isSaved);
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  if (!apartment) return null;

  const images = apartment.images?.length > 0 ? apartment.images : ['/images/placeholder-apartment.jpg'];

  const styles = {
    container: { maxWidth: '1280px', margin: '0 auto', padding: '24px' },
    header: { marginBottom: '24px' },
    title: { fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px' },
    meta: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', color: '#555' },
    actions: { display: 'flex', gap: '12px', marginTop: '16px' },
    actionBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '8px', background: 'white', cursor: 'pointer' },
    gallery: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px', borderRadius: '16px', overflow: 'hidden', marginBottom: '40px', maxHeight: '500px' },
    mainImage: { width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' },
    sideImages: { display: 'grid', gridTemplateRows: '1fr 1fr', gap: '8px' },
    sideImage: { width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' },
    content: { display: 'grid', gridTemplateColumns: '1fr 380px', gap: '48px' },
    main: {},
    sidebar: {},
    section: { marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #eee' },
    sectionTitle: { fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px' },
    hostInfo: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
    highlights: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' },
    highlight: { textAlign: 'center', padding: '16px', background: '#f7f7f7', borderRadius: '12px' },
    highlightIcon: { fontSize: '1.5rem', marginBottom: '8px', color: '#1a3a5c' },
    amenities: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
    amenity: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' },
    bookingCard: { position: 'sticky', top: '100px', background: 'white', border: '1px solid #ddd', borderRadius: '16px', padding: '24px', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' },
    priceDisplay: { display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '16px' },
    price: { fontSize: '1.5rem', fontWeight: '700' },
    perNight: { color: '#555' },
    dateInputs: { display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid #ddd', borderRadius: '8px', marginBottom: '12px' },
    dateInput: { padding: '12px', borderRight: '1px solid #ddd' },
    dateLabel: { fontSize: '0.75rem', fontWeight: '600', marginBottom: '4px' },
    guestInput: { border: '1px solid #ddd', borderRadius: '8px', padding: '12px', marginBottom: '16px' },
    bookBtn: { width: '100%', padding: '14px', background: '#1a3a5c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' },
    pricingSummary: { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' },
    pricingRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px' },
    pricingTotal: { display: 'flex', justifyContent: 'space-between', fontWeight: '700', paddingTop: '12px', borderTop: '1px solid #eee', marginTop: '12px' }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>{apartment.name}</h1>
        <div style={styles.meta}>
          {apartment.reviewCount > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FiStar style={{ fill: '#1a3a5c', color: '#1a3a5c' }} />
              <strong>{apartment.averageRating?.toFixed(1)}</strong>
              <span>({apartment.reviewCount} reviews)</span>
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FiMapPin />
            {apartment.address?.city}, {apartment.address?.postcode}
          </span>
        </div>
        <div style={styles.actions}>
          <button style={styles.actionBtn} onClick={handleSave}>
            <FiHeart style={{ fill: isSaved ? '#1a3a5c' : 'none', color: isSaved ? '#1a3a5c' : 'inherit' }} />
            {isSaved ? 'Saved' : 'Save'}
          </button>
          <button style={styles.actionBtn}>
            <FiShare2 /> Share
          </button>
        </div>
      </header>

      {/* Gallery */}
      <div style={styles.gallery} className="apartment-gallery">
        <img src={images[0]} alt={apartment.name} style={styles.mainImage} />
        <div style={styles.sideImages}>
          {images.slice(1, 3).map((img, idx) => (
            <img key={idx} src={img} alt={`${apartment.name} ${idx + 2}`} style={styles.sideImage} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content} className="apartment-content">
        <div style={styles.main}>
          {/* Highlights */}
          <div style={styles.highlights} className="highlights-grid">
            <div style={styles.highlight}>
              <FiHome style={styles.highlightIcon} />
              <div style={{ fontWeight: '600' }}>{apartment.type}</div>
              <div style={{ color: '#555', fontSize: '0.875rem' }}>{apartment.bedrooms} bedroom{apartment.bedrooms !== 1 ? 's' : ''}</div>
            </div>
            <div style={styles.highlight}>
              <FiUsers style={styles.highlightIcon} />
              <div style={{ fontWeight: '600' }}>{apartment.maxGuests} guests</div>
              <div style={{ color: '#555', fontSize: '0.875rem' }}>{apartment.beds} bed{apartment.beds !== 1 ? 's' : ''}</div>
            </div>
            <div style={styles.highlight}>
              <FiStar style={styles.highlightIcon} />
              <div style={{ fontWeight: '600' }}>{apartment.averageRating?.toFixed(1) || 'New'}</div>
              <div style={{ color: '#555', fontSize: '0.875rem' }}>{apartment.reviewCount} reviews</div>
            </div>
          </div>

          {/* Description */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>About this apartment</h2>
            <p style={{ color: '#555', lineHeight: '1.8' }}>{apartment.description}</p>
          </div>

          {/* Amenities */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>What this place offers</h2>
            <div style={styles.amenities}>
              {apartment.amenities?.map((amenity, idx) => (
                <div key={idx} style={styles.amenity}>
                  <FiCheck style={{ color: '#1a3a5c' }} />
                  <span>{amenity.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* House Rules */}
          {apartment.houseRules?.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>House Rules</h2>
              <ul style={{ paddingLeft: '20px', color: '#555' }}>
                {apartment.houseRules.map((rule, idx) => (
                  <li key={idx} style={{ marginBottom: '8px' }}>{rule}</li>
                ))}
              </ul>
              <p style={{ marginTop: '16px' }}>
                <strong>Check-in:</strong> {apartment.checkInTime} &nbsp;|&nbsp;
                <strong>Check-out:</strong> {apartment.checkOutTime}
              </p>
            </div>
          )}

          {/* Reviews */}
          {reviews.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <FiStar style={{ fill: '#1a3a5c', color: '#1a3a5c', marginRight: '8px' }} />
                {apartment.averageRating?.toFixed(1)} &middot; {apartment.reviewCount} reviews
              </h2>
              <div style={{ display: 'grid', gap: '24px' }}>
                {reviews.slice(0, 4).map(review => (
                  <div key={review._id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f7f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {review.user?.firstName?.[0] || 'U'}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600' }}>{review.user?.firstName} {review.user?.lastName?.[0]}.</div>
                        <div style={{ color: '#555', fontSize: '0.875rem' }}>
                          {new Date(review.createdAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    <p style={{ color: '#555' }}>{review.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Booking Sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.bookingCard}>
            <div style={styles.priceDisplay}>
              <span style={styles.price}>£{apartment.pricePerNight}</span>
              <span style={styles.perNight}>/ night</span>
            </div>

            <div style={styles.dateInputs}>
              <div style={styles.dateInput}>
                <div style={styles.dateLabel}>CHECK-IN</div>
                <DatePicker
                  selected={checkIn}
                  onChange={setCheckIn}
                  selectsStart
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={new Date()}
                  placeholderText="Add date"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
              <div style={{ ...styles.dateInput, borderRight: 'none' }}>
                <div style={styles.dateLabel}>CHECK-OUT</div>
                <DatePicker
                  selected={checkOut}
                  onChange={setCheckOut}
                  selectsEnd
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={checkIn || new Date()}
                  placeholderText="Add date"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>

            <div style={styles.guestInput}>
              <div style={styles.dateLabel}>GUESTS</div>
              <select
                value={guests.adults}
                onChange={(e) => setGuestsState({ ...guests, adults: parseInt(e.target.value) })}
                style={{ width: '100%', border: 'none', fontSize: '1rem' }}
              >
                {Array.from({ length: apartment.maxGuests }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n} guest{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>

            <button style={styles.bookBtn} onClick={handleBook} disabled={calculating}>
              {calculating ? 'Calculating...' : 'Reserve'}
            </button>

            {pricing && (
              <div style={styles.pricingSummary}>
                <div style={styles.pricingRow}>
                  <span>£{apartment.pricePerNight} x {pricing.nights} nights</span>
                  <span>£{pricing.basePrice.toFixed(2)}</span>
                </div>
                {pricing.discount > 0 && (
                  <div style={{ ...styles.pricingRow, color: '#008A05' }}>
                    <span>Discount</span>
                    <span>-£{pricing.discount.toFixed(2)}</span>
                  </div>
                )}
                {pricing.cleaningFee > 0 && (
                  <div style={styles.pricingRow}>
                    <span>Cleaning fee</span>
                    <span>£{pricing.cleaningFee.toFixed(2)}</span>
                  </div>
                )}
                <div style={styles.pricingRow}>
                  <span>Service fee</span>
                  <span>£{pricing.serviceFee.toFixed(2)}</span>
                </div>
                <div style={styles.pricingTotal}>
                  <span>Total</span>
                  <span>£{pricing.totalPrice.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 992px) {
          .apartment-gallery { grid-template-columns: 1fr !important; }
          .apartment-content { grid-template-columns: 1fr !important; }
          .highlights-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default ApartmentDetail;
